import React, { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
const ChatWidget = lazy(() => import('./components/ChatWidget'));
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';

// ── Code-split pages (loaded on demand) ───────────────────────
const HomePage = lazy(() => import('./pages/HomePage'));
const HomePageVans = lazy(() => import('./pages/HomePageVans'));
const HomePageMotorcycles = lazy(() => import('./pages/HomePageMotorcycles'));
const HomePageMotorhomes = lazy(() => import('./pages/HomePageMotorhomes'));
const HomePageCaravans = lazy(() => import('./pages/HomePageCaravans'));
const HomePagePlant = lazy(() => import('./pages/HomePagePlant'));
const HomePageFarm = lazy(() => import('./pages/HomePageFarm'));
const ListingsPage = lazy(() => import('./pages/ListingsPage'));
const PostAdPage = lazy(() => import('./pages/PostAdPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ViewListingPage = lazy(() => import('./pages/ViewListingPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const EditListingPage = lazy(() => import('./pages/EditListingPage'));
const CheckinPage = lazy(() => import('./pages/CheckinPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const FavouritesPage = lazy(() => import('./pages/FavouritesPage'));
const SiteAdminPage = lazy(() => import('./pages/SiteAdminPage'));
const SupportDashboardPage = lazy(() => import('./pages/SupportDashboardPage'));
const TransactionModelPage = lazy(() => import('./pages/TransactionModelPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const UserSearchResultsPage = lazy(() => import('./pages/UserSearchResultsPage'));
const ReviewReportPage = lazy(() => import('./pages/ReviewReportPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const EmailTestPage = lazy(() => import('./pages/EmailTestPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const BusinessLoginPage = lazy(() => import('./pages/BusinessLoginPage'));
const BusinessRegistrationPage = lazy(() => import('./pages/BusinessRegistrationPage'));
const BusinessDashboardPage = lazy(() => import('./pages/BusinessDashboardPage'));
const BusinessLandingPage = lazy(() => import('./pages/BusinessLandingPage'));
const BranchProfilePage = lazy(() => import('./pages/BranchProfilePage'));
const SupportTicketsPage = lazy(() => import('./pages/SupportTicketsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const WordPressPluginPage = lazy(() => import('./pages/WordPressPluginPage'));
const SocialCheckoutLinksPage = lazy(() => import('./pages/SocialCheckoutLinksPage'));
const StandaloneCheckoutPage = lazy(() => import('./pages/StandaloneCheckoutPage'));

import ScrollToTop from './components/ScrollToTop';

// Lightweight loading spinner
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

// Inner component so useLocation() is called inside <BrowserRouter>
function AppRoutes() {
  const location = useLocation();
  return (
    <SiteSettingsProvider>
      <ChatProvider>
        <ChatWidget />
        <ErrorBoundary>
          {/* key on Suspense forces a fresh boundary each navigation, so the
              fallback spinner shows immediately instead of React keeping the
              old page visible during startTransition lazy-load. */}
          <Suspense key={location.pathname} fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/cars" element={<HomePage />} />
              <Route path="/vans" element={<HomePageVans />} />
              <Route path="/motorcycles" element={<HomePageMotorcycles />} />
              <Route path="/motorhomes" element={<HomePageMotorhomes />} />
              <Route path="/caravans" element={<HomePageCaravans />} />
              <Route path="/plant" element={<HomePagePlant />} />
              <Route path="/farm" element={<HomePageFarm />} />
              <Route path="/listings" element={<ListingsPage />} />
              <Route path="/listings/:id" element={<ViewListingPage />} />
              <Route path="/search/users" element={<UserSearchResultsPage />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/post-ad" element={<ProtectedRoute><PostAdPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/edit-listing/:listingId" element={<ProtectedRoute><EditListingPage /></ProtectedRoute>} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
              <Route path="/my-bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
              <Route path="/bookings-from-others" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/signup" element={<RegisterPage />} />
              <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/checkin/:bookingId/:qrCode" element={<CheckinPage />} />
              <Route path="/checkin/:bookingId" element={<CheckinPage />} />
              <Route path="/favourites" element={<ProtectedRoute><FavouritesPage /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/support/tickets" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />
              <Route path="/support/tickets/:ticketId" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />
              <Route path="/internal/console" element={<ProtectedRoute><SiteAdminPage /></ProtectedRoute>} />
              <Route path="/internal/support" element={<ProtectedRoute><SupportDashboardPage /></ProtectedRoute>} />
              <Route path="/transaction-model" element={<TransactionModelPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/email-test" element={<EmailTestPage />} />
              <Route path="/review/:booking_id" element={<ReviewPage />} />
              <Route path="/user/:id" element={<UserProfilePage />} />
              <Route path="/report-review/:reviewId" element={<ReviewReportPage />} />
              {/* Business routes */}
              <Route path="/business" element={<BusinessLandingPage />} />
              <Route path="/business/login" element={<BusinessLoginPage />} />
              <Route path="/business/register" element={<BusinessRegistrationPage />} />
              <Route path="/business/branch/:branchId" element={<BranchProfilePage />} />
              <Route path="/business/dashboard" element={<BusinessDashboardPage />} />
              {/* Legal & info pages */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/wordpress-plugin" element={<ProtectedRoute><WordPressPluginPage /></ProtectedRoute>} />
              <Route path="/social-links" element={<ProtectedRoute><SocialCheckoutLinksPage /></ProtectedRoute>} />
              <Route path="/checkout/:id" element={<StandaloneCheckoutPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </ChatProvider>
    </SiteSettingsProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;